// src/App.jsx (Fixed with correct payment routes)

import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { CartProvider } from './context/CartContext';
import { ToastProvider } from './context/ToastContext';
import { DiscountProvider } from './context/DiscountContext';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import Layout from './components/common/Layout';
import Loading from './components/common/Loading';
import ToastContainer from './components/ui/Toast';
import ErrorBoundary from "./components/common/ErrorBoundary";
import { OrganizationStructuredData, WebsiteStructuredData } from './components/seo/StructuredData';
import CookieConsent from './components/CookieConsent';
import AnalyticsProvider from './components/analytics/AnalyticsProvider';
import { installMetaPixelGuard } from './utils/metaConversions';
import { CartPromotionBadgeProvider } from './context/CartPromotionBadgeContext';
// import ExclusiveEsansGiftPopup from './components/promotions/ExclusiveEsansGiftPopup';


// Import lazy-loaded pages
const HomePage = React.lazy(() => import('./pages/HomePage'));
const ProductListingPage = React.lazy(() => import('./pages/ProductListingPageOptimal'));
const ProductDetailPage = React.lazy(() => import('./pages/ProductDetailPage'));
const CartPage = React.lazy(() => import('./pages/CartPage'));
const CheckoutPage = React.lazy(() => import('./pages/CheckoutPage'));
const OrderTrackingSearchPage = React.lazy(() => import('./pages/OrderTrackingSearchPage'));
const OrderTrackingPage = React.lazy(() => import('./pages/OrderTrackingPage'));
const FaqPage = React.lazy(() => import('./pages/FaqPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const ConfiguratorPage = React.lazy(() => import('./pages/ConfiguratorPage'));
// const CampaignsPage = React.lazy(() => import('./pages/CampaignsPage'));
const BlogPage = React.lazy(() => import('./pages/BlogPage'));
const BlogDetailPage = React.lazy(() => import('./pages/BlogDetailPage'));

const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const PaymentSuccessPage = React.lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentFailurePage = React.lazy(() => import('./pages/PaymentFailurePage'));
const PaymentCallbackPage = React.lazy(() => import('./pages/PaymentCallbackPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const DistanceSalesAgreementPage = React.lazy(() => import('./pages/DistanceSalesAgreementPage'));
const WarrantyReturnPage = React.lazy(() => import('./pages/WarrantyReturnPage'));
const MembershipAgreement = React.lazy(() => import('./pages/MembershipAgreement'));
// Removed ToptanSatisPage - wholesale not offered

// Authentication pages
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const EmailVerificationPage = React.lazy(() => import('./pages/EmailVerificationPage'));

// User account pages  
const AccountPage = React.lazy(() => import('./pages/AccountPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const AddressesPage = React.lazy(() => import('./pages/AddressesPage'));
const FavoritesPage = React.lazy(() => import('./pages/FavoritesPage'));
const CustomListsPage = React.lazy(() => import('./pages/CustomListsPage'));
const ListViewPage = React.lazy(() => import('./pages/ListViewPage'));
const OrdersPage = React.lazy(() => import('./pages/OrdersPage'));
const OrderDetailPage = React.lazy(() => import('./pages/OrderDetailPage'));

// App version for localStorage management
const APP_VERSION = '1.9.0';

function App() {
  useEffect(() => {
    installMetaPixelGuard();
    const guardInterval = window.setInterval(installMetaPixelGuard, 500);
    const stopGuard = window.setTimeout(() => window.clearInterval(guardInterval), 10000);
    return () => {
      window.clearInterval(guardInterval);
      window.clearTimeout(stopGuard);
    };
  }, []);

  // Version-based localStorage clearing
  useEffect(() => {
    const storedVersion = localStorage.getItem('appVersion');
    if (storedVersion !== APP_VERSION) {
      console.log(`🔄 App version updated from ${storedVersion || 'unknown'} to ${APP_VERSION}. Clearing localStorage...`);
      
      // Clear all localStorage except for essential data
      const essentialKeys = ['language', 'i18nextLng', 'cookieConsent']; // Keep language settings and cookie consent
      const allKeys = Object.keys(localStorage);
      
      allKeys.forEach(key => {
        if (!essentialKeys.includes(key)) {
          localStorage.removeItem(key);
        }
      });
      
      // Set language to Turkish for all users on new deployment
      localStorage.setItem('i18nextLng', 'tr');
      localStorage.setItem('language', 'tr');
      
      // Set new version
      localStorage.setItem('appVersion', APP_VERSION);
      

    }
  }, []);

  return (
    <Router>
      <ErrorBoundary>
        <HelmetProvider>
          {/* Global SEO structured data */}
          <OrganizationStructuredData />
          <WebsiteStructuredData />
          <ToastProvider>
            <AuthProvider>
              <DiscountProvider>
                <CartProvider>
                  <CartPromotionBadgeProvider>
                  <Suspense fallback={<Loading />}>
                <Routes>
                {/* Payment callback route - no layout needed */}
                <Route path="/payment/callback" element={<PaymentCallbackPage />} />

                {/* All other routes with layout */}
                <Route path="/*" element={
                  <AnalyticsProvider>
                    <Layout>
                      <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/products" element={<ProductListingPage />} />
                      <Route path="/products/:productId" element={<ProductDetailPage />} />
                      <Route path="/cart" element={<CartPage />} />
                      <Route path="/checkout" element={<CheckoutPage />} />
                      <Route path="/orders/search" element={<OrderTrackingSearchPage />} />
                      <Route path="/orders/order-tracking" element={<OrderTrackingPage />} />
                      <Route path="/orders/track/:orderNumber" element={<OrderTrackingPage />} />
                      <Route path="/faq" element={<FaqPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/configurator" element={<ConfiguratorPage />} />
                      {/* <Route path="/campaigns" element={<CampaignsPage />} /> */}
                      <Route path="/blog" element={<BlogPage />} />
                      <Route path="/blog/:slug" element={<BlogDetailPage />} />

                      {/* Authentication Routes */}
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/register" element={<RegisterPage />} />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                      <Route path="/reset-password" element={<ResetPasswordPage />} />
                      <Route path="/verify-email" element={<EmailVerificationPage />} />

                      {/* Protected User Account Routes */}
                      <Route path="/account" element={
                        <ProtectedRoute>
                          <AccountPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/account/profile" element={
                        <ProtectedRoute>
                          <ProfilePage />
                        </ProtectedRoute>
                      } />
                      <Route path="/account/addresses" element={
                        <ProtectedRoute>
                          <AddressesPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/account/favorites" element={
                        <ProtectedRoute>
                          <FavoritesPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/account/lists" element={
                        <ProtectedRoute>
                          <CustomListsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/account/lists/:listId" element={
                        <ProtectedRoute>
                          <ListViewPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/account/orders" element={
                        <ProtectedRoute>
                          <OrdersPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/account/orders/:orderId" element={
                        <ProtectedRoute>
                          <OrderDetailPage />
                        </ProtectedRoute>
                      } />

                      {/* Company Pages */}
                      <Route path="/about" element={<AboutPage />} />
                      
                      {/* Legal Pages */}
                      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                      <Route path="/distance-sales-agreement" element={<DistanceSalesAgreementPage />} />
                      <Route path="/warranty-return" element={<WarrantyReturnPage />} />
                      <Route path="/membership-agreement" element={<MembershipAgreement />} />

                      {/* Payment result pages - ADD THESE MISSING ROUTES */}
                      <Route path="/payment/success" element={<PaymentSuccessPage />} />
                      <Route path="/payment/failure" element={<PaymentFailurePage />} />

                      {/* Legacy order routes (keep for backward compatibility) */}
                      <Route path="/order/success" element={<PaymentSuccessPage />} />
                      <Route path="/order/failed" element={<PaymentFailurePage />} />
                      <Route path="/order/cancelled" element={<PaymentFailurePage />} />

                      {/* 404 page */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Layout>
                  </AnalyticsProvider>
                } />
              </Routes>
              <ToastContainer />
              <CookieConsent />
              {/* <ExclusiveEsansGiftPopup /> */}

                  </Suspense>
                  </CartPromotionBadgeProvider>
                </CartProvider>
              </DiscountProvider>
            </AuthProvider>
          </ToastProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </Router>
  );
}

export default App;